import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import colors, { appColors } from 'styles/colors';
import sizes from 'styles/sizes';
import borders from 'styles/borders';
import EditIcon from '@mui/icons-material/BorderColor';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ErrorIcon from '@mui/icons-material/Error';
import { setDragImageToCenterOfElement } from 'utils/dragImage';
import {
  DropIndicator,
  DragOrientation,
  getChipDropPosition,
  DRAG_TARGET_ATTR,
} from './dragDropUtils';
import { HP_STEP_BLUE, HP_ERROR_BG, HP_ERROR_BORDER, TooltipContainer } from './Common';
import type { BaseStepChipProps } from './Common';

const DEFAULT_BG_COLOR = colors.gray2;
const RENAME_BG_COLOR = HP_STEP_BLUE;
const ERROR_COLOR = appColors.error;

// Semantic attribute carrying the chip's step index, used by StepsConfigSection to scroll the
// active chip into view. Decouples that lookup from test ids.
export const STEP_CHIP_INDEX_ATTR = 'data-step-index';

const ChipContainer = styled.div<{
  $active?: boolean;
  $isRenaming?: boolean;
  $disabled?: boolean;
  $isDragging?: boolean;
  $hasError?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  background-color: ${(props) => {
    if (props.$hasError) return HP_ERROR_BG;
    if (props.$isRenaming) return RENAME_BG_COLOR;
    return DEFAULT_BG_COLOR;
  }};
  border-radius: 8px;
  box-sizing: border-box;
  padding: 8px 10px 8px 8px;
  gap: 8px;
  min-height: 48px;
  border: 1px solid
    ${(props) => {
      if (props.$hasError) return HP_ERROR_BORDER;
      if (props.$isRenaming) return colors.blue4;
      if (props.$active) return colors.gray10;
      return colors.gray4;
    }};
  cursor: ${(props) => (props.$disabled ? 'default' : 'pointer')};
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease;

  ${(props) =>
    props.$isDragging &&
    `
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
    z-index: 100;
    background-color: ${props.$hasError ? HP_ERROR_BG : colors.gray5};
  `}

  &:hover {
    border-color: ${(props) => (props.$hasError ? HP_ERROR_BORDER : colors.gray8)};

    .action-icons {
      opacity: 1;
    }
  }

  ${(props) =>
    props.$disabled &&
    `
    border-color: ${DEFAULT_BG_COLOR};
    span {
      color: ${colors.gray10}A3;
    }
    &:hover {
      border-color: ${DEFAULT_BG_COLOR};
    }
  `}
`;

const DragHandle = styled.div<{ $isDragging?: boolean; $hasError?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  ${(props) =>
    props.$isDragging &&
    `
    cursor: grabbing;
  `}

  svg {
    width: 16px;
    height: 16px;
    color: ${(props) => (props.$hasError ? HP_ERROR_BORDER : `${colors.gray10}A3`)};
  }
`;

const StepName = styled.span`
  flex: 1;
  font-size: ${sizes.textCaption};
  color: ${colors.gray10};
  user-select: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StepNameInput = styled.input<{ $hasError?: boolean }>`
  flex: 1;
  font-size: ${sizes.textCaption};
  color: ${(props) => (props.$hasError ? ERROR_COLOR : colors.gray10)};
  background: transparent;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;

  &::placeholder {
    color: ${colors.gray7};
  }
`;

const ErrorIconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 2px;
  flex-shrink: 0;
`;

const ErrorTooltip = styled(TooltipContainer)`
  position: fixed;
  bottom: auto;
  display: block;
  transform: translateX(-50%) translateY(calc(-100% - 8px));
  pointer-events: none;

  &::after {
    left: 50%;
    transform: translateX(-50%);
  }
`;

const ActionIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.15s ease;
`;

const IconButton = styled.button<{ $variant?: 'default' | 'danger' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  border-radius: ${borders.borderRadius.medium};

  svg {
    width: 16px;
    height: 16px;
    color: ${(props) => (props.$variant === 'danger' ? ERROR_COLOR : colors.gray8)};
  }

  &:hover {
    svg {
      color: ${(props) => (props.$variant === 'danger' ? colors.red7 : colors.gray10)};
    }
  }
`;

const EditIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
    color: ${colors.gray8};
  }
`;

export interface StepConfigButtonProps extends BaseStepChipProps {
  /** Whether the chip is in rename mode */
  isRenaming?: boolean;
  /** Called when the edit/rename button is clicked */
  onEdit?: () => void;
  /** Called when the delete button is clicked */
  onDelete?: () => void;
  /** Called when the name is changed during rename mode */
  onNameChange?: (newName: string) => void;
  /** Called when rename is confirmed (Enter key or blur) */
  onRenameConfirm?: (newName: string) => void;
  /** Called when rename is cancelled (Escape key) */
  onRenameCancel?: () => void;
}

export const StepConfigButton = ({
  stepName,
  active,
  disabled,
  isRenaming,
  onClick,
  onEdit,
  onDelete,
  onNameChange,
  onRenameConfirm,
  onRenameCancel,
  validateName,
  index,
  isDragging,
  dropPosition,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
}: StepConfigButtonProps) => {
  const [localName, setLocalName] = useState(stepName);
  const inputRef = useRef<HTMLInputElement>(null);
  const chipRef = useRef<HTMLDivElement>(null);
  const errorIconRef = useRef<HTMLDivElement>(null);
  const [errorTooltipPos, setErrorTooltipPos] = useState<{ top: number; left: number } | null>(
    null
  );

  const errorMessage = isRenaming && validateName ? validateName(localName) : null;
  const hasError = !!errorMessage;

  useEffect(() => {
    if (!isRenaming) setLocalName(stepName);
  }, [stepName, isRenaming]);

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (!hasError) onRenameConfirm?.(localName);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setLocalName(stepName);
      onRenameCancel?.();
    }
  };

  const handleBlur = () => {
    if (!hasError) onRenameConfirm?.(localName);
    // error state: do nothing — keep rename mode open so the user can fix the name
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalName(newValue);
    onNameChange?.(newValue);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.();
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      if (index !== undefined && onDragStart) {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(index));

        if (chipRef.current) {
          setDragImageToCenterOfElement(e.dataTransfer, chipRef.current);
        }

        onDragStart(index);
      }
    },
    [index, onDragStart]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (index !== undefined && onDragOver && chipRef.current) {
        onDragOver(index, getChipDropPosition(DragOrientation.Vertical, e, chipRef.current));
      }
    },
    [index, onDragOver]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (index !== undefined && onDrop) {
        onDrop(index);
      }
    },
    [index, onDrop]
  );

  const handleDragEnd = useCallback(() => {
    onDragEnd?.();
  }, [onDragEnd]);

  const handleContainerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <ChipContainer
      ref={chipRef}
      data-testid={index !== undefined ? `edit-step-chip-${index}` : undefined}
      {...{ [DRAG_TARGET_ATTR]: '' }}
      {...(index !== undefined ? { [STEP_CHIP_INDEX_ATTR]: index } : {})}
      role="button"
      tabIndex={disabled ? -1 : 0}
      $active={active}
      $isRenaming={isRenaming}
      $disabled={disabled}
      $isDragging={isDragging}
      $hasError={hasError}
      onClick={disabled ? undefined : onClick}
      onKeyDown={disabled || isRenaming ? undefined : handleContainerKeyDown}
      onMouseDown={
        isRenaming
          ? (e) => {
              if (e.target !== inputRef.current) e.preventDefault();
            }
          : undefined
      }
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {dropPosition && (
        <DropIndicator $position={dropPosition} $orientation={DragOrientation.Horizontal} />
      )}
      <DragHandle
        draggable={!disabled && index !== undefined && onDragStart != null}
        $isDragging={isDragging}
        $hasError={hasError}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <DragIndicatorIcon viewBox="5 2 14 20" />
      </DragHandle>

      {isRenaming ? (
        <StepNameInput
          ref={inputRef}
          value={localName}
          $hasError={hasError}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <StepName>{stepName}</StepName>
      )}

      {isRenaming && hasError ? (
        <>
          {(onEdit || onDelete) && (
            <ActionIcons className="action-icons" style={{ pointerEvents: 'none' }}>
              {onEdit && (
                <IconButton
                  type="button"
                  disabled
                  $variant="danger"
                  aria-label={`Edit step ${stepName}`}
                >
                  <EditIcon />
                </IconButton>
              )}
              {onDelete && (
                <IconButton
                  type="button"
                  disabled
                  $variant="danger"
                  aria-label={`Delete step ${stepName}`}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </ActionIcons>
          )}
          <ErrorIconWrapper
            ref={errorIconRef}
            onMouseEnter={() => {
              const rect = errorIconRef.current?.getBoundingClientRect();
              if (rect) {
                setErrorTooltipPos({ top: rect.top, left: rect.left + rect.width / 2 });
              }
            }}
            onMouseLeave={() => setErrorTooltipPos(null)}
          >
            {errorTooltipPos != null &&
              createPortal(
                <ErrorTooltip style={{ top: errorTooltipPos.top, left: errorTooltipPos.left }}>
                  {errorMessage}
                </ErrorTooltip>,
                document.body
              )}
            <ErrorIcon sx={{ fontSize: 16, color: ERROR_COLOR, flexShrink: 0 }} />
          </ErrorIconWrapper>
        </>
      ) : isRenaming ? (
        <EditIconContainer>
          <EditIcon />
        </EditIconContainer>
      ) : !disabled && (onEdit || onDelete) ? (
        <ActionIcons className="action-icons">
          {onEdit && (
            <IconButton
              type="button"
              onClick={handleEditClick}
              aria-label={`Edit step ${stepName}`}
            >
              <EditIcon />
            </IconButton>
          )}
          {onDelete && (
            <IconButton
              type="button"
              $variant="danger"
              onClick={handleDeleteClick}
              aria-label={`Delete step ${stepName}`}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </ActionIcons>
      ) : !onEdit && !onDelete ? (
        <EditIconContainer>
          <EditIcon />
        </EditIconContainer>
      ) : null}
    </ChipContainer>
  );
};
